import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/components/theme/ThemeProvider";

import * as gtag from "@/lib/gtag";

// suas imports de páginas e contextos
import Index from "@/pages/home";
import NotFound from "@/pages/not-found";
import Terms from "@/pages/terms";
import Privacy from "@/pages/privacy";
import Features from "@/pages/features";
import HowToUse from "@/pages/how-to-use";
import Pricing from "@/pages/pricing";
import About from "@/pages/about";
import KiwifyPage from "@/pages/kiwify";
import ThankYouPage from "@/pages/thank-you";

import Login from "@/pages/auth/login";
import Register from "@/pages/auth/register";

import Dashboard from "@/pages/dashboard/dashboard";
import Profile from "@/pages/dashboard/profile";
import Subscription from "@/pages/dashboard/subscription";
import Prompts from "@/pages/dashboard/prompts";
import PromptDetail from "@/pages/dashboard/prompt-detail";

import Agenda from "@/pages/dashboard/agenda";
import Clients from "@/pages/dashboard/clients";
import ClientDetail from "@/pages/dashboard/client-detail";
import Appointments from "@/pages/dashboard/appointments";

import { AuthProvider } from "@/contexts/AuthContext";
import RequireAuth from "@/components/auth/RequireAuth";
import HotmartPage from "./pages/hotmart";
import MonetizzePage from "./pages/monetizze";

const queryClient = new QueryClient();

// Componente para escutar rota e disparar pageview
const GAListener = () => {
  const location = useLocation();

  useEffect(() => {
    gtag.pageview(location.pathname + location.search);
  }, [location]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <GAListener />
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/features" element={<Features />} />
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/kiwify" element={<KiwifyPage />} />
              <Route path="/hotmart" element={<HotmartPage />} />
              <Route path="/monetizze" element={<MonetizzePage />} />
              <Route path="/obrigado" element={<ThankYouPage />} />

              {/* Auth routes */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />

              {/* Dashboard routes - protected */}
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/dashboard/profile" element={<RequireAuth><Profile /></RequireAuth>} />
              <Route path="/dashboard/subscription" element={<RequireAuth><Subscription /></RequireAuth>} />
              <Route path="/dashboard/prompts" element={<RequireAuth><Prompts /></RequireAuth>} />
              <Route path="/dashboard/prompts/:id" element={<RequireAuth><PromptDetail /></RequireAuth>} />

              {/* Agenda routes - protected */}
              <Route path="/dashboard/agenda" element={<RequireAuth><Agenda /></RequireAuth>} />
              <Route path="/dashboard/clients" element={<RequireAuth><Clients /></RequireAuth>} />
              <Route path="/dashboard/clients/:id" element={<RequireAuth><ClientDetail /></RequireAuth>} />
              <Route path="/dashboard/appointments" element={<RequireAuth><Appointments /></RequireAuth>} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
